import { acceptHMRUpdate, defineStore } from "pinia";
import { useModelStore } from "@editor/items/model/model.store";
import { isSameItem } from "@editor/items/itemFunctions";

export type MainSelectionType = "incident" | "action" | "scenario";
export type SubSelectionType =
  | "effect"
  | "condition"
  | "scenarioEnd"
  | "stakeholder"
  | "incidentTrigger"
  | "requirement";
export type MainSelection = {
  type: MainSelectionType;
  id: number;
};
export type SubSelection = {
  type: SubSelectionType;
  id: number;
};

export const useVariablesViewStore = defineStore("model/variablesView", {
  state: () => ({
    /**
     * The main selection of the variables view (content of the first menu)
     * Action, Incident, Scenario
     */
    mainSelectedItems: [] as MainSelection[],

    /**
     * Sub selection in this view (content of the second menu)
     * Effect, Condition, ScenarioEnd, Stakeholder
     */
    subSelectedItems: [] as SubSelection[],

    /**
     * Sub-Sub selection in this view (content of the third menu)
     * Condition
     */
    subSubSelectedItems: [] as {
      type: "condition";
      id: number;
    }[],

    docs: {}
  }),

  getters: {
    mainSelection(): MainSelection | null {
      if (this.mainSelectedItems.length === 1) {
        return this.mainSelectedItems[0];
      }
      return null;
    },

    subSelection(): SubSelection | null {
      if (this.subSelectedItems.length === 1) {
        return this.subSelectedItems[0];
      }
      return null;
    },

    subSubSelection(): {
      type: "condition";
      id: number;
    } | null {
      if (this.subSubSelectedItems.length === 1) {
        return this.subSubSelectedItems[0];
      }
      return null;
    }
  },

  actions: {
    /**
     * utility method to deselect deleted items.
     * used in undo: deselect item when create was undone.
     */
    deselectDeletedItems() {
      if (this.mainSelection == null) return;

      const modelStore = useModelStore();
      if (
        modelStore.getItem(this.mainSelection.type, this.mainSelection.id) ==
        null
      ) {
        this.selectMain(null);
      }
      if (this.subSelection == null) return;
      if (
        modelStore.getItem(this.subSelection.type, this.subSelection.id) == null
      ) {
        this.selectSub(null);
      }
    },

    // selection
    selectMain(item: MainSelection | null) {
      if (item == null || isSameItem(item, this.mainSelection)) {
        this.mainSelectedItems = [];
        this.subSelectedItems = [];
        this.subSubSelectedItems = [];
      } else {
        this.mainSelectedItems = [{ type: item.type, id: item.id }];
        this.subSelectedItems = [];
        this.subSubSelectedItems = [];
      }
    },
    selectMainMany(items: MainSelection[]) {
      this.mainSelectedItems = items.map((item) => ({ type: item.type, id: item.id }));
      this.subSelectedItems = [];
      this.subSubSelectedItems = [];
    },
    selectSub(item: SubSelection | null) {
      if (item == null || isSameItem(item, this.subSelection)) {
        this.subSelectedItems = [];
        this.subSubSelectedItems = [];
      } else {
        this.subSelectedItems = [{ type: item.type, id: item.id }];
        this.subSubSelectedItems = [];
      }
    },
    selectSubSub(
      item: {
        type: "condition";
        id: number;
      } | null
    ) {
      if (item == null || isSameItem(item, this.subSubSelection)) {
        this.subSubSelectedItems = [];
      } else {
        this.subSubSelectedItems = [{ type: item.type, id: item.id }];
      }
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    acceptHMRUpdate(useVariablesViewStore as any, import.meta.hot)
  );
}
